import React from 'react';
import { Card, CardContent, CardHeader, Typography, TextField, Autocomplete, Button, Grid, Box, ButtonGroup } from '@mui/material';
import { Globe, Settings, Factory, ArrowRight, Calendar,Building2 } from 'lucide-react';
import { useTheme } from '@mui/material/styles';
import car from "../images/car.png";
import equipment from "../images/boilder.png"
import location from "../images/Location.jpg"
import { PieChart, Pie, Cell, Legend, Tooltip, } from 'recharts';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import FootprintAnalytics from './AnalyticsReductionTargets/FootprintAnalytics';

const CustomCard = ({
  image,
  title,
  subtitleI,
  onClick,
  direction = 'rtl',
  buttonText = "View Details",
  link
}) => {
  const navigate = useNavigate();
  return (
    <div className="border-2 rounded-lg overflow-hidden max-w-lg h-64">
      <div
        className={`flex ${direction === 'rtl' ? 'flex-row-reverse' : 'flex-row'} cursor-pointer h-[85%] bg-green-50`}
        onClick={() => { navigate(link) }}
      >
        <div className="flex flex-col w-3/5 p-3">
          <div className="flex-1">
            <div className="flex gap-2 text-base font-bold text-gray-900 mb-1">
              {title}
            </div>
            <div className="text-xs font-medium text-gray-500">
              {subtitleI}
            </div>
          </div>
        </div>
        <div className="w-4/5">
          <img
            src={image}
            alt={`${title} album cover`}
            className="w-full h-full object-cover"
          />
        </div>
      </div>

      <button
        className="w-full bg-green-600 hover:bg-green-700 text-white py-2 flex items-center justify-center gap-2 transition-colors"
        onClick={() => { navigate(link) }}
      >
        <span className="text-sm">{buttonText}</span>
        <ArrowRight className="w-3 h-3" />
      </button>
    </div>
  );
};

const InfoCard = ({
  image,
  imageAlt = 'Card image',
  heading,
  text,
  buttonText,
  onButtonClick
}) => {
  return (
    <Card className="max-w-sm bg-white border-2 border-green-300 bg-green-50 flex flex-col h-[400px] transform transition-all duration-300 ease-in-out hover:scale-105 hover:shadow-xl hover:bg-green-100 cursor-pointer">
      <div className="p-6 flex flex-col flex-grow">
        {image && (
          <div className="mb-4 p-2">
            <img
              src={image}
              alt={imageAlt}
              className="h-40 w-full object-cover rounded-md"
            />
          </div>
        )}

        {heading && (
          <h3 className="text-xl font-semibold mb-2">
            {heading}
          </h3>
        )}

        {text && (
          <p className="text-gray-600 mb-4 flex-grow">
            {text}
          </p>
        )}

        {buttonText && (
          <div className="flex justify-center items-center mt-auto">
            <button
              onClick={onButtonClick}
              className="flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white font-semibold py-2 px-4 rounded-md shadow-md transition-colors"
            >
              {buttonText}
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    </Card>
  );
};

const ClimateProfile = () => {
  const [activeTab, setActiveTab] = React.useState(0);
  const navigate = useNavigate();
  const user = useSelector((state) => state.user.user);

  const data = [
    { name: 'Scope 1', value: 54736.84, color: '#FFD700' },
    { name: 'Scope 2', value: 9467.38, color: '#9370DB' },
    { name: 'Scope 3', value: 10220, color: '#FFB6C1' }
  ];

  const total = data.reduce((sum, item) => sum + item.value, 0).toFixed(2);


  const cardData = [
    {
      title: "Add Locations",
      description: "Locations will be instrumental in building your scope 2 (indirect emissions) footprint.",
      image: location, // Replace with actual image URL
      buttonText: "Locations",
      link: "/account/locations"
    },
    {
      title: "Add Vehicles",
      description: "Vehicles form the backbone of your scope 1 (direct emissions) footprint.",
      image: car, // Replace with actual image URL
      buttonText: "Vehicles",
      link: "/account/vehicles"
    },
    {
      title: "Add Equipments",
      description: "Equipments form the backbone of your scope 1 (direct emissions) footprint.",
      image: equipment,
      buttonText: "Equipments",
      link: "/account/equipment"
    },
  ];


  const cardData2 = [
    {
      title: "Determine which report are right for your organization",
      description: "",
      image: "https://placehold.co/600x400", // Replace with actual image URL
      buttonText: "Take The Assesment",
    },
    {
      title: "CDP",
      description: "Carbon Disclosure Project for measuring environmental impacts.",
      image: "https://placehold.co/600x400",
      buttonText: "Get Started",
    },
    {
      title: "CSRD",
      description: "Corporate sustainablity reporting Directive for the EU.",
      image: "https://placehold.co/600x400",
      buttonText: "Get Started",
    },
    {
      title: "TCFD",
      description: "Task Force On climate related Financial Disclosure",
      image: "https://placehold.co/600x400",
      buttonText: "",
    },
  ];

  return (
    <div>
      {!user?.company?.id ?  <Card 
        className="w-full h-64 cursor-pointer hover:bg-gray-50 transition-colors duration-200"
        onClick={()=>navigate('/account/company-details')}
      >
        <CardContent className="h-full flex flex-col items-center justify-center p-6 text-center">
          <div className="mb-4 p-3 bg-green-100 rounded-full">
            <Building2 className="w-8 h-8 text-green-600" />
          </div>
          
          <h3 className="text-xl font-semibold mb-2">
            Complete Your Company Profile
          </h3>
          
          <p className="text-gray-600 mb-4 max-w-sm">
            Add your company details to unlock climate insights and track your environmental impact
          </p>
          
          <div className="flex items-center text-green-600 font-medium group">
            Get Started 
            <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
          </div>
        </CardContent>
      </Card> : <Card
        sx={{
          width: '100%',
          height: '250px',
          borderRadius: '10px',
          border: '2px solid rgb(122, 176, 101)',
          boxShadow: 'none'
        }}
      >
        <div className="mx-auto px-10 py-10">
          <div className="mt-15 -space-y-px">
            <CardHeader
              title={<Typography variant="h6">Climate Profile</Typography>}
              sx={{ py: 0 }}
            />
            <CardHeader
              title={
                <Typography variant="h4" fontWeight="bold">
                  {user?.company?.name}
                </Typography>
              }
              sx={{ py: 0 }}
            />
          </div>
          <hr className="my-10 border-gray-500" />
          <div className="flex items-center space-x-8 mb-10">
            <div className="flex items-center space-x-2">
              <Factory className="h-5 w-5" />
              <span>
                {[
                  user.company?.other_sector || user?.company?.sector_name || "",
                  user?.company?.other_industry || user?.company?.industry_name || "",
                  user.company?.other_subind || user?.company?.sub_industry_name || ""
                ].filter(Boolean).join(" ")}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <Globe className="h-5 w-5" />
              <span>{user?.company?.hqLocation}</span>
            </div>
          </div>
        </div>
      </Card>}

      <div className="flex justify-between items-center mt-10">
        <div className="flex space-x-2">
          <ButtonGroup variant="" aria-label="Navigation buttons" style={{ border: "2px solid #388E3C" }}>
            <Button style={{ backgroundColor: activeTab === 0 ? "#388E3C" : "", color: activeTab === 0 ? "#fff" : "", }} color="green" onClick={() => setActiveTab(0)}>
              Home
            </Button>
            <Button style={{ backgroundColor: activeTab === 1 ? "#388E3C" : "", color: activeTab === 1 ? "#fff" : "", }} color="gray" onClick={() => setActiveTab(1)}>
              Share Footprint
            </Button>
            <Button style={{ backgroundColor: activeTab === 2 ? "#388E3C" : "", color: activeTab === 2 ? "#fff" : "", }} color="gray" onClick={() => setActiveTab(2)}>
              Reports
            </Button>
            <Button style={{ backgroundColor: activeTab === 3 ? "#388E3C" : "", color: activeTab === 3 ? "#fff" : "", }} color="gray" onClick={() => setActiveTab(3)}>
              Ratings
            </Button>
            <Button style={{ backgroundColor: activeTab === 4 ? "#388E3C" : "", color: activeTab === 4 ? "#fff" : "", }} color="gray" onClick={() => setActiveTab(4)}>
              Targets
            </Button>
          </ButtonGroup>
        </div>
        <Button
          className="p-2"
          style={{ minWidth: 0, border: "2px solid #388E3C" }}
          color='green'
          onClick={() => { navigate('/account/data-reporting-year') }}
        >
          <Calendar className="h-5 w-5" color='green' />
        </Button>
      </div>

      <div>
        <Typography variant="h5" fontWeight="bold" className='p-5'>
          Let's get started
        </Typography>

        <div className="grid grid-cols-4 gap-4 mt-4">
          {cardData.map((card, index) => (
            <CustomCard link={card.link} key={index} image={card.image} title={card.title} subtitleI={card.description} direction='ltr' buttonText={card.buttonText} />
          ))}
        </div>
      </div>
      <div>
        <Typography variant="h5" fontWeight="bold" className='p-5'>
          Initial Emissions Estimate
        </Typography>
        <Card className="w-full border-2 border-green-500" sx={{ backgroundColor: "#F0FDF4" }}>
          <FootprintAnalytics standalone={false}/>
        </Card>
      </div>

      <div>
        <Typography variant="h5" fontWeight="bold" className='p-5'>
          Disclosures
        </Typography>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
          {cardData2.map((item) => (
            <InfoCard
              key={item.title}
              image={item.image}
              imageAlt="CDP Logo"
              heading={item.title}
              text={item.description}
              buttonText={item.buttonText}
              onButtonClick={() => console.log('Button clicked')}
              className="w-[300px] h-[400px]" // Fixed width & height
            />
          ))}
        </div>

      </div>

    </div>
  );
};

export default ClimateProfile;